import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Video,
  Upload,
  Download,
  Plus,
  Save,
  Trash2,
  Edit,
  FileJson,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Settings,
  ExternalLink,
  Copy,
} from "lucide-react";

import { API } from "../../utils/api";

const AdminModulesManager = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Video URL editing
  const [editingModule, setEditingModule] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  
  // JSON Import
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState("");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  
  // Create new module
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newModule, setNewModule] = useState({
    number: "",
    title: "",
    description: "",
    video_url: "",
    module_type: "main",
    parent_module_number: null,
  });

  useEffect(() => {
    fetchModules();
  }, [token]);

  const fetchModules = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/modules`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setModules(data);
      } else {
        toast.error("Erreur lors du chargement des modules");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  // Save video URL
  const handleSaveVideoUrl = async (moduleId) => {
    setSaving(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/modules/${moduleId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ video_url: videoUrl }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("URL vidéo mise à jour");
        setEditingModule(null);
        setVideoUrl("");
        fetchModules();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setSaving(false);
    }
  };

  // Export modules
  const handleExport = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/modules/export`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `flow-modules-export-${new Date().toISOString().split("T")[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Modules exportés");
      }
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  // Import modules from JSON
  const handleImport = async () => {
    if (!jsonInput.trim()) {
      toast.error("Veuillez entrer du JSON valide");
      return;
    }

    setImportLoading(true);
    try {
      let importData;
      try {
        const parsed = JSON.parse(jsonInput);
        // Handle both formats: direct array or wrapped in { modules: [...] }
        importData = {
          modules: Array.isArray(parsed) ? parsed : (parsed.modules || [parsed]),
          replace_existing: replaceExisting
        };
      } catch (e) {
        toast.error("JSON invalide");
        setImportLoading(false);
        return;
      }

      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/modules/import`, {
        method: "POST",
        headers,
        body: JSON.stringify(importData),
        credentials: "include",
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message);
        if (result.errors?.length > 0) {
          result.errors.forEach(err => toast.error(err));
        }
        setImportDialogOpen(false);
        setJsonInput("");
        fetchModules();
      } else {
        toast.error(result.detail || "Erreur lors de l'import");
      }
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setImportLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setJsonInput(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  // Create new module
  const handleCreateModule = async (e) => {
    e.preventDefault();
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const moduleData = {
        ...newModule,
        number: parseInt(newModule.number),
        parent_module_number: newModule.parent_module_number 
          ? parseInt(newModule.parent_module_number) 
          : null,
      };

      const response = await fetch(`${API}/admin/modules`, {
        method: "POST",
        headers,
        body: JSON.stringify(moduleData),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Module créé");
        setCreateDialogOpen(false);
        setNewModule({
          number: "",
          title: "",
          description: "",
          video_url: "",
          module_type: "main",
          parent_module_number: null,
        });
        fetchModules();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la création");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    }
  };

  // Delete module
  const handleDeleteModule = async (moduleId, moduleTitle) => {
    if (!confirm(`Supprimer le module "${moduleTitle}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/modules/${moduleId}`, {
        method: "DELETE",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Module supprimé");
        fetchModules();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    }
  };

  // Copy JSON template
  const copyJsonTemplate = () => {
    const template = `[
  {
    "number": 1,
    "title": "Titre du module",
    "description": "Description du module",
    "video_url": "https://www.loom.com/embed/xxx",
    "module_type": "main",
    "content_blocks": [
      {
        "type": "text",
        "title": "Introduction",
        "content": "Contenu de l'introduction...",
        "target_type": "common",
        "target_value": null
      },
      {
        "type": "text",
        "title": "Pour les Analyseurs",
        "content": "Contenu spécifique...",
        "target_type": "base",
        "target_value": "Analyseur"
      }
    ],
    "exercises": [
      {
        "title": "Exercice 1",
        "description": "Description de l'exercice",
        "instructions": "Instructions détaillées...",
        "target_phase": null,
        "is_optional": false
      }
    ]
  }
]`;
    navigator.clipboard.writeText(template);
    toast.success("Template JSON copié !");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#101E4D]"></div>
        </div>
      </div>
    );
  }

  const mainModules = modules.filter(m => m.module_type !== "annex");
  const annexModules = modules.filter(m => m.module_type === "annex");

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <main className="flex-1 p-6 lg:p-8 lg:ml-64" data-testid="admin-modules-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#101E4D]">
              Gestion des Modules
            </h1>
            <p className="text-slate-600">
              Configurez les vidéos et importez/exportez le contenu
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={handleExport}
              className="border-[#101E4D] text-[#101E4D]"
              data-testid="export-modules-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter JSON
            </Button>
            
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-[#FF6B35] text-[#FF6B35]"
                  data-testid="import-modules-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importer JSON
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center">
                    <FileJson className="w-5 h-5 mr-2 text-[#FF6B35]" />
                    Importer des modules
                  </DialogTitle>
                  <DialogDescription>
                    Collez votre JSON ou téléchargez un fichier
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* File upload */}
                  <div className="flex gap-3">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".json"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Charger un fichier JSON
                    </Button>
                    <Button
                      variant="outline"
                      onClick={copyJsonTemplate}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copier template
                    </Button>
                  </div>

                  {/* JSON input */}
                  <div className="space-y-2">
                    <Label>JSON des modules</Label>
                    <Textarea
                      value={jsonInput}
                      onChange={(e) => setJsonInput(e.target.value)}
                      placeholder='[{"number": 1, "title": "Module 1", ...}]'
                      rows={15}
                      className="font-mono text-sm"
                      data-testid="json-input"
                    />
                  </div>

                  {/* Options */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="replace"
                      checked={replaceExisting}
                      onChange={(e) => setReplaceExisting(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <Label htmlFor="replace" className="text-sm">
                      Remplacer les modules existants (par numéro)
                    </Label>
                  </div>

                  {/* Help text */}
                  <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-600">
                    <p className="font-medium mb-2">Format attendu :</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><code>number</code> : Numéro du module (obligatoire)</li>
                      <li><code>title</code> : Titre du module (obligatoire)</li>
                      <li><code>description</code> : Description</li>
                      <li><code>video_url</code> : URL Loom (embed)</li>
                      <li><code>module_type</code> : "main" ou "annex"</li>
                      <li><code>content_blocks</code> : Liste des blocs de contenu</li>
                      <li><code>exercises</code> : Liste des exercices</li>
                    </ul>
                  </div>

                  <Button
                    onClick={handleImport}
                    disabled={importLoading || !jsonInput.trim()}
                    className="w-full bg-[#FF6B35] hover:bg-[#E85D2A]"
                    data-testid="confirm-import-btn"
                  >
                    {importLoading ? "Import en cours..." : "Importer les modules"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#101E4D] hover:bg-[#0A1330]"
                  data-testid="create-module-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau module
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Créer un nouveau module</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateModule} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Numéro</Label>
                      <Input
                        type="number"
                        value={newModule.number}
                        onChange={(e) => setNewModule({ ...newModule, number: e.target.value })}
                        required
                        data-testid="new-module-number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select
                        value={newModule.module_type}
                        onValueChange={(v) => setNewModule({ ...newModule, module_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="main">Principal</SelectItem>
                          <SelectItem value="annex">Annexe</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Titre</Label>
                    <Input
                      value={newModule.title}
                      onChange={(e) => setNewModule({ ...newModule, title: e.target.value })}
                      required
                      data-testid="new-module-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newModule.description}
                      onChange={(e) => setNewModule({ ...newModule, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>URL Vidéo Loom (embed)</Label>
                    <Input
                      value={newModule.video_url}
                      onChange={(e) => setNewModule({ ...newModule, video_url: e.target.value })}
                      placeholder="https://www.loom.com/embed/..."
                    />
                  </div>
                  {newModule.module_type === "annex" && (
                    <div className="space-y-2">
                      <Label>Module parent (numéro)</Label>
                      <Input
                        type="number"
                        value={newModule.parent_module_number || ""}
                        onChange={(e) => setNewModule({ ...newModule, parent_module_number: e.target.value })}
                        placeholder="Ex: 2"
                      />
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-[#101E4D]">
                    Créer le module
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="videos" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger
              value="videos"
              className="data-[state=active]:bg-[#101E4D] data-[state=active]:text-white"
            >
              <Video className="w-4 h-4 mr-2" />
              URLs Vidéos
            </TabsTrigger>
            <TabsTrigger
              value="modules"
              className="data-[state=active]:bg-[#101E4D] data-[state=active]:text-white"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Liste des modules
            </TabsTrigger>
          </TabsList>

          {/* Videos Tab */}
          <TabsContent value="videos">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center text-[#101E4D]">
                  <Video className="w-5 h-5 mr-2 text-[#FF6B35]" />
                  Configuration des vidéos Loom
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {modules.map((module) => (
                    <div
                      key={module.module_id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                      data-testid={`video-row-${module.module_id}`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-[#101E4D] rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold">
                            {module.module_type === "annex" ? `A${module.number}` : module.number}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium text-[#101E4D]">{module.title}</h4>
                          <p className="text-sm text-slate-500">
                            {module.module_type === "annex" ? "Module annexe" : "Module principal"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        {editingModule === module.module_id ? (
                          <>
                            <Input
                              value={videoUrl}
                              onChange={(e) => setVideoUrl(e.target.value)}
                              placeholder="https://www.loom.com/embed/..."
                              className="w-80"
                              data-testid={`video-input-${module.module_id}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleSaveVideoUrl(module.module_id)}
                              disabled={saving}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingModule(null);
                                setVideoUrl("");
                              }}
                            >
                              Annuler
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center space-x-2">
                              {module.video_url ? (
                                <>
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                  <span className="text-sm text-green-600 font-medium">Configurée</span>
                                  <a
                                    href={module.video_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-slate-400 hover:text-[#FF6B35]"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-5 h-5 text-orange-500" />
                                  <span className="text-sm text-orange-600">Non configurée</span>
                                </>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingModule(module.module_id);
                                setVideoUrl(module.video_url || "");
                              }}
                              data-testid={`edit-video-${module.module_id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Modifier
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Help */}
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <h4 className="font-medium text-blue-800 mb-2">Comment obtenir l'URL Loom ?</h4>
                  <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
                    <li>Ouvrez votre vidéo sur Loom</li>
                    <li>Cliquez sur "Partager" puis "Embed"</li>
                    <li>Copiez l'URL dans le format : <code className="bg-blue-100 px-1 rounded">https://www.loom.com/embed/xxxxx</code></li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Modules List Tab */}
          <TabsContent value="modules">
            <div className="space-y-6">
              {/* Main Modules */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-[#101E4D]">
                    Modules principaux ({mainModules.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {mainModules.map((module) => (
                      <div
                        key={module.module_id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-[#101E4D] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-lg">{module.number}</span>
                          </div>
                          <div>
                            <h4 className="font-medium text-[#101E4D]">{module.title}</h4>
                            <p className="text-sm text-slate-500">
                              {module.content_blocks?.length || 0} blocs • {module.exercises?.length || 0} exercices
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/content/${module.module_id}`)}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Contenu
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteModule(module.module_id, module.title)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Annex Modules */}
              {annexModules.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-[#101E4D]">
                      Modules annexes ({annexModules.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {annexModules.map((module) => (
                        <div
                          key={module.module_id}
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-[#FF6B35] rounded-lg flex items-center justify-center">
                              <span className="text-white font-bold text-lg">A{module.number}</span>
                            </div>
                            <div>
                              <h4 className="font-medium text-[#101E4D]">{module.title}</h4>
                              <p className="text-sm text-slate-500">
                                Lié au module {module.parent_module_number} • {module.content_blocks?.length || 0} blocs
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/admin/content/${module.module_id}`)}
                            >
                              <Settings className="w-4 h-4 mr-1" />
                              Contenu
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteModule(module.module_id, module.title)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminModulesManager;
